import { useRef, useState } from "react";
import { Loader2, Upload, Plus, Trash2, Table2, Eye } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { formatEuroIt } from "../lib/euro";
import { Button } from "./ui/button";
import { TabellaInvaliditaPreview } from "./TabellaInvaliditaPreview";

const CFG = {
  micro: { title: "TABELLA MICRO INVALIDITÀ", range: "da 1% a 9%", lo: 1, hi: 9 },
  macro: { title: "TABELLA MACRO INVALIDITÀ", range: "da 10% a 100%", lo: 10, hi: 100 },
};
const inp = "h-8 w-full rounded-md border border-slate-700 bg-slate-900/60 px-2 text-sm text-slate-100";

const ManualRows = ({ tipo, onPreview }) => {
  const c = CFG[tipo];
  const [rows, setRows] = useState([{ eta: "", punti: "", valore: "" }]);
  const set = (i, k, v) => setRows((r) => r.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  const go = () => {
    const celle = rows.filter((r) => r.eta && r.punti && r.valore).map((r) => [Number(r.eta), Number(r.punti), Number(String(r.valore).replace(/\./g, "").replace(",", "."))]);
    if (!celle.length) { toast.error("Compila almeno una riga completa"); return; }
    if (celle.some(([, p]) => p < c.lo || p > c.hi)) { toast.error(`I punti devono essere ${c.range}`); return; }
    onPreview({ anno: String(new Date().getFullYear()), celle, avvisi: [] });
  };
  return (
    <div className="space-y-2" data-testid={`tinv-${tipo}-manual`}>
      <div className="grid grid-cols-[1fr_1fr_1.4fr_32px] gap-1.5 text-[11px] uppercase text-slate-400"><span>Età</span><span>Punti %</span><span>Valore €</span><span /></div>
      {rows.map((r, i) => (
        <div key={i} className="grid grid-cols-[1fr_1fr_1.4fr_32px] gap-1.5">
          <input value={r.eta} onChange={(e) => set(i, "eta", e.target.value.replace(/\D/g, ""))} className={inp} data-testid={`tinv-${tipo}-row-${i}-eta`} />
          <input value={r.punti} onChange={(e) => set(i, "punti", e.target.value.replace(/\D/g, ""))} className={inp} placeholder={`${c.lo}-${c.hi}`} data-testid={`tinv-${tipo}-row-${i}-punti`} />
          <input value={r.valore} onChange={(e) => set(i, "valore", e.target.value.replace(/[^\d.,]/g, ""))} className={inp} placeholder="es. 2.174,59" data-testid={`tinv-${tipo}-row-${i}-valore`} />
          <button type="button" onClick={() => setRows((x) => (x.length > 1 ? x.filter((_, j) => j !== i) : [{ eta: "", punti: "", valore: "" }]))} className="text-slate-400 hover:text-red-400" data-testid={`tinv-${tipo}-row-${i}-del`}><Trash2 className="h-4 w-4" /></button>
        </div>
      ))}
      <div className="flex gap-2">
        <Button type="button" size="sm" variant="outline" onClick={() => setRows((x) => [...x, { eta: "", punti: "", valore: "" }])} className="border-slate-700 text-slate-200 hover:bg-slate-800" data-testid={`tinv-${tipo}-add-row`}><Plus className="h-4 w-4 mr-1" />Riga</Button>
        <Button type="button" size="sm" onClick={go} className="bg-slate-700 hover:bg-slate-600 text-white" data-testid={`tinv-${tipo}-manual-preview`}><Eye className="h-4 w-4 mr-1" />Anteprima</Button>
      </div>
    </div>
  );
};

// Gestione di una tabella invalidità (micro o macro) in Importazione Dati.
export const TabelleInvaliditaCard = ({ tipo, tabelle, reload }) => {
  const c = CFG[tipo];
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [manual, setManual] = useState(false);

  const onFile = async (f) => {
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post(`/admin/tabelle-invalidita/${tipo}/anteprima`, fd);
      setPreview(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const del = async (anno) => {
    if (!window.confirm(`Eliminare la tabella ${tipo.toUpperCase()} ${anno}? La Quantificazione tornerà al calcolo con punto base.`)) return;
    try { await api.delete(`/admin/tabelle-invalidita/${tipo}/${anno}`); toast.success("Tabella eliminata"); reload(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="rounded-xl border border-slate-700 bg-slate-900/40 p-5 space-y-4" data-testid={`tinv-${tipo}-card`}>
      <div className="flex items-center gap-2 text-slate-100 font-head font-semibold"><Table2 className="h-5 w-5 text-amber-400" />{c.title}</div>
      <p className="text-xs text-slate-400">Danno biologico per invalidità permanente {c.range}: valore in € per ogni età e punto, una tabella per anno. Usata dalla Quantificazione della pratica (se manca il valore resta il calcolo con punto base). File CSV, Excel o ODS con riga dei punti e colonna delle età.</p>
      <div className="space-y-1.5" data-testid={`tinv-${tipo}-list`}>
        {!tabelle.length && <div className="text-xs text-slate-500">Nessuna tabella caricata.</div>}
        {tabelle.map((t) => (
          <div key={t.anno} className="flex items-center gap-2 rounded-md border border-slate-700 bg-slate-900/60 px-3 py-2 text-sm" data-testid={`tinv-${tipo}-anno-${t.anno}`}>
            <span className="font-semibold text-amber-300">{t.anno}</span>
            <span className="text-slate-300">{t.celle} valori · età {t.eta_min}–{t.eta_max} · punti {t.punti_min}–{t.punti_max}</span>
            {t.punto_base != null && <span className="text-xs text-slate-400">· punto base {formatEuroIt(t.punto_base)}</span>}
            <button type="button" onClick={() => del(t.anno)} title="Elimina tabella" className="ml-auto text-slate-400 hover:text-red-400" data-testid={`tinv-${tipo}-del-${t.anno}`}><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
      </div>
      <div className="flex flex-wrap gap-2">
        <Button type="button" onClick={() => fileRef.current?.click()} disabled={busy} className="bg-amber-500 hover:bg-amber-600 text-[#0a0d14] font-semibold" data-testid={`tinv-${tipo}-upload`}>
          {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Upload className="h-4 w-4 mr-1" />}Carica file
        </Button>
        <Button type="button" variant="outline" onClick={() => setManual((m) => !m)} className="border-slate-700 text-slate-200 hover:bg-slate-800" data-testid={`tinv-${tipo}-manual-toggle`}><Plus className="h-4 w-4 mr-1" />Inserimento manuale</Button>
        <input ref={fileRef} type="file" accept=".csv,.txt,.xlsx,.xls,.ods" className="hidden" onChange={(e) => onFile(e.target.files?.[0])} data-testid={`tinv-${tipo}-file-input`} />
      </div>
      {manual && <ManualRows tipo={tipo} onPreview={setPreview} />}
      {preview && <TabellaInvaliditaPreview tipo={tipo} data={preview} onClose={() => setPreview(null)} onDone={reload} />}
    </div>
  );
};
